import { ResourceLibraryItemCreationPayload } from "@design-controls/types";
import { useMutation } from "react-query";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { ResourceLibraryAPI } from "../../api/resourceLibrary";
import { useCurrentUser, useToastProvider } from "@qualio/ui-components";
import { getQRI } from "@qualio/search-qri";

const isRegistryItemDuplicateError = (error: unknown): boolean =>
  axios.isAxiosError(error) &&
  error.response?.status === 400 &&
  error.response?.data?.type === "RegistryItemInvalidDisplayID";

export const useCreateResourceLibraryProduct = () => {
  const { t } = useTranslation();
  const { showToast } = useToastProvider();
  const { companyId } = useCurrentUser();
  return useMutation({
    mutationFn: async ({
      displayId,
      name,
    }: ResourceLibraryItemCreationPayload) => {
      const { id } = await ResourceLibraryAPI.createProduct(
        companyId,
        displayId,
        name,
      );
      const product = await ResourceLibraryAPI.getProductById(companyId, id);

      return {
        ...product,
        matrixQRI: buildVersionlessProductQri(
          companyId,
          product.registry_item_matrix_id,
        ),
      };
    },
    onError: (error: unknown) => {
      if (isRegistryItemDuplicateError(error)) {
        showToast({
          title: t("products.notifications.duplicateRegistryId.title"),
          description: t(
            "products.notifications.duplicateRegistryId.description",
          ),
          status: "error",
          "data-cy": "toast-product-create-duplicate-id-error",
        });
        return;
      }
      showToast({
        title: t("products.notifications.createError.title"),
        description: t("products.notifications.createError.description"),
        status: "error",
        "data-cy": "toast-product-create-error",
      });
    },
  });
};

const buildVersionlessProductQri = (
  companyId: number,
  matrixId: string,
): string =>
  getQRI({
    qriBaseUrl:
      import.meta.env.VITE_QRI_SERVICE_PUBLIC_URL ??
      import.meta.env.VITE_QRI_SERVICE_HOSTNAME,
    domain: "registry",
    domainType: "registry-item",
    companyId: companyId.toString(10),
    id: matrixId,
  });
